import React from 'react';

import { bind } from 'bind-event-listener';

import { getDocument } from '@atlaskit/browser-apis';
import { cssMap } from '@atlaskit/css';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { TOOLBAR_RANK, TOOLBARS, VIEW_MODE_TOGGLE_SECTION } from '@atlaskit/editor-common/toolbar';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { ReadonlyTransaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import type { RegisterComponent } from '@atlaskit/editor-toolbar-model';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { layers } from '@atlaskit/theme/constants';
import { token } from '@atlaskit/tokens';

import type {
	MarkdownModePlugin,
	MarkdownModePluginOptions,
	MarkdownModePluginState,
	MarkdownModeView,
} from './markdownModePluginType';
import { buildGlobalKeydownHandler } from './pm-plugins/keymap';
import { markdownModePluginKey } from './pm-plugins/main';
import { MarkdownModeContentComponent } from './ui/MarkdownModeContentComponent';
import { MarkdownModeViewToggle } from './ui/MarkdownModeViewToggle';

type MarkdownModeApi = ExtractInjectionAPI<MarkdownModePlugin> | undefined;

// Pinning rule: any non-WYSIWYG view (source / preview) hides the PM editor,
// so a floating selection toolbar has nothing to anchor to.
const contextualFormattingOverrideForView = (
	view: MarkdownModeView,
): 'always-pinned' | 'controlled' => (view === 'wysiwyg' ? 'controlled' : 'always-pinned');

// Mirrors `selection-toolbar/PrimaryToolbarComponent`: prefers
// `userPreferences.toolbarDockingPosition` when the new plugin is enabled,
// otherwise falls back to `selectionToolbar.toolbarDocking`. In source /
// preview view the primary toolbar is forcibly pinned via the toolbar
// override (see `setView` below), so we treat the toolbar as docked-to-top
// regardless of the user's saved preference — otherwise the view-switcher
// renders in-content while the toolbar is actually pinned at the top.
const useIsToolbarDockedToTop = (api: MarkdownModeApi): boolean => {
	const { dockingPositionFromPrefs, dockingFromSelectionToolbar, view } =
		useSharedPluginStateWithSelector(
			api,
			['userPreferences', 'selectionToolbar', 'markdownMode'],
			(states) => ({
				dockingPositionFromPrefs: states.userPreferencesState?.preferences?.toolbarDockingPosition,
				dockingFromSelectionToolbar: states.selectionToolbarState?.toolbarDocking,
				view: states.markdownModeState?.view,
			}),
		);
	if (view && view !== 'wysiwyg' && fg('platform_editor_toolbar_mode_override')) {
		return true;
	}
	if (fg('platform_editor_use_preferences_plugin')) {
		return dockingPositionFromPrefs === 'top';
	}
	return dockingFromSelectionToolbar === 'top';
};

const MarkdownModeContentComponentWrapper = ({
	editorView,
	config,
	api,
}: {
	api: MarkdownModeApi;
	config: MarkdownModePluginOptions;
	editorView: EditorView;
}) => {
	const currentView = useSharedPluginStateWithSelector(
		api,
		['markdownMode'],
		(states) => states.markdownModeState?.view ?? ('wysiwyg' as MarkdownModeView),
	);

	return (
		<>
			<MarkdownModeContentComponent
				editorView={editorView}
				config={config}
				currentView={currentView}
			/>
			<InContentToggle api={api} />
		</>
	);
};

const styles = cssMap({
	trailingSection: {
		position: 'absolute',
		insetInlineEnd: token('space.150'),
		insetBlockStart: '50%',
		transform: 'translateY(-50%)',
		display: 'flex',
		alignItems: 'center',
	},
	// `pointer-events: none` so the row never intercepts clicks on the editor surface beneath it.
	inContentToggleStickyRow: {
		position: 'fixed',
		insetBlockStart: token('space.100'),
		insetInlineEnd: token('space.300'),
		zIndex: layers.card(),
		pointerEvents: 'none',
	},
	inContentToggleAnchor: {
		display: 'flex',
		alignItems: 'center',
		paddingBlock: token('space.050'),
		paddingInline: token('space.050'),
		backgroundColor: token('elevation.surface'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		pointerEvents: 'auto',
	},
});

const InToolbarToggle = ({ api }: { api: MarkdownModeApi }) => {
	const isDockedToTop = useIsToolbarDockedToTop(api);
	if (!isDockedToTop) {
		return null;
	}
	return (
		<Box xcss={styles.trailingSection} testId="markdown-mode-view-toggle-section">
			<MarkdownModeViewToggle api={api} />
		</Box>
	);
};

export const InContentToggle = ({ api }: { api: MarkdownModeApi }): React.ReactElement | null => {
	const isDockedToTop = useIsToolbarDockedToTop(api);
	if (isDockedToTop) {
		return null;
	}
	return (
		<Box xcss={styles.inContentToggleStickyRow}>
			<Box xcss={styles.inContentToggleAnchor} testId="markdown-mode-view-toggle-in-content">
				<MarkdownModeViewToggle api={api} />
			</Box>
		</Box>
	);
};

const buildToolbarComponents = (
	config: MarkdownModePluginOptions | undefined,
	api: MarkdownModeApi,
): RegisterComponent[] => {
	if (!config?.isMarkdownMode) {
		return [];
	}

	return [
		{
			type: VIEW_MODE_TOGGLE_SECTION.type,
			key: VIEW_MODE_TOGGLE_SECTION.key,
			parents: [
				{
					type: 'toolbar',
					key: TOOLBARS.PRIMARY_TOOLBAR,
					rank: TOOLBAR_RANK[VIEW_MODE_TOGGLE_SECTION.key],
				},
			],
			component: () => <InToolbarToggle api={api} />,
		},
	];
};

export const markdownModePlugin: MarkdownModePlugin = ({ config, api }) => {
	let editorView: EditorView | undefined;
	const setEditorView = (newEditorView: EditorView) => {
		editorView = newEditorView;
	};

	// Resolve the initial view ONCE, at plugin construction time — not in
	// `init()`, which re-runs on `state.reconfigure` and would clobber any
	// in-flight user choice on every preset rebuild.
	const initialView: MarkdownModeView =
		config?.viewModePersistence?.getInitialView?.() ?? 'wysiwyg';

	api?.toolbar?.actions.registerComponents(buildToolbarComponents(config, api));

	return {
		name: 'markdownMode',

		getSharedState(editorState) {
			if (!editorState) {
				return {
					view: initialView,
					isMarkdownMode: config?.isMarkdownMode ?? false,
				};
			}
			return (
				markdownModePluginKey.getState(editorState) ?? {
					view: initialView,
					isMarkdownMode: config?.isMarkdownMode ?? false,
				}
			);
		},

		actions: {
			setView(view: MarkdownModeView) {
				if (!editorView) {
					return;
				}
				const prevView = markdownModePluginKey.getState(editorView.state)?.view ?? initialView;
				if (prevView === view) {
					return;
				}
				const tr = editorView.state.tr;
				if (fg('platform_editor_toolbar_mode_override')) {
					api?.toolbar?.commands.setContextualFormattingModeOverride(
						contextualFormattingOverrideForView(view),
					)({ tr });
				}
				tr.setMeta(markdownModePluginKey, { view });
				editorView.dispatch(tr);
				config?.viewModePersistence?.onViewChange?.(view, prevView);
			},
		},

		contentComponent({ editorView: contentEditorView }) {
			if (!config?.isMarkdownMode || !contentEditorView) {
				return null;
			}
			return (
				<MarkdownModeContentComponentWrapper
					editorView={contentEditorView}
					config={config}
					api={api}
				/>
			);
		},

		pmPlugins() {
			return [
				{
					name: 'markdownModeState',
					plugin: () => {
						return new SafePlugin<MarkdownModePluginState>({
							key: markdownModePluginKey,
							state: {
								init: () => ({
									view: initialView,
									isMarkdownMode: config?.isMarkdownMode ?? false,
								}),
								apply(tr: ReadonlyTransaction, currentState: MarkdownModePluginState) {
									const meta = tr.getMeta(markdownModePluginKey) as
										| { view: MarkdownModeView }
										| undefined;
									if (meta) {
										return { ...currentState, view: meta.view };
									}
									return currentState;
								},
							},
							view(newEditorView: EditorView) {
								setEditorView(newEditorView);
								let destroyed = false;

								// Seed the toolbar override on first mount so a page that
								// opens straight into source/preview pins the toolbar without
								// needing a user-triggered `setView`.
								if (config?.isMarkdownMode && fg('platform_editor_toolbar_mode_override')) {
									// Deferred to a microtask: dispatching synchronously inside
									// `view()` is a PM re-entry hazard — the meta can be dropped
									// before downstream plugins' `apply()` runs. By the next
									// microtask the editor is fully constructed and dispatch is safe.
									// The `destroyed` guard prevents dispatching on a torn-down
									// view if `destroy()` runs before the microtask fires (e.g.
									// React StrictMode double-mount).
									Promise.resolve().then(() => {
										if (destroyed) {
											return;
										}
										const tr = newEditorView.state.tr;
										api?.toolbar?.commands.setContextualFormattingModeOverride(
											contextualFormattingOverrideForView(initialView),
										)({ tr });
										newEditorView.dispatch(tr);
									});
								}

								// Register a global keydown listener for the toggle shortcut.
								// We use document-level rather than a PM keymap because in
								// source view the PM editor is hidden/blurred and PM keymaps
								// never fire.
								const handler = buildGlobalKeydownHandler(api, () => editorView);
								const doc = getDocument();
								const unbind = doc ? bind(doc, { type: 'keydown', listener: handler }) : () => { };

								return {
									destroy() {
										destroyed = true;
										unbind();
									},
								};
							},
						});
					},
				},
			];
		},
	};
};
